import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Styled components
const PortalContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  min-height: 100vh;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
`;

const PortalTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(90deg, #2563eb, #7c3aed);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`;

const PortalSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #64748b;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 4rem;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 2rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1e293b;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const DashboardPreview = styled(motion.div)`
  margin-top: 5rem;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
`;

const DashboardImage = styled(motion.img)`
  width: 100%;
  border-radius: 16px;
  display: block;
`;

const CTA = styled(motion.div)`
  text-align: center;
  margin-top: 5rem;
  padding: 3rem;
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 16px;
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6
    }
  }
};

const CenterPortal = () => {
  const features = [
    {
      icon: "📊",
      title: "Analytics Dashboard",
      description: "Real-time data visualization and performance metrics for your centers."
    },
    {
      icon: "📅",
      title: "Scheduling System",
      description: "Easily manage appointments, classes, and staff schedules."
    },
    {
      icon: "💬",
      title: "Communication Hub",
      description: "Integrated messaging system for staff, students, and parents."
    },
    {
      icon: "📝",
      title: "Document Management",
      description: "Secure storage and sharing of important documents and reports."
    },
    {
      icon: "💰",
      title: "Billing & Payments",
      description: "Streamlined invoicing and payment processing system."
    },
    {
      icon: "📱",
      title: "Mobile Access",
      description: "Full portal functionality available on any mobile device."
    }
  ];

  return (
    <PortalContainer>
      <Header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <PortalTitle
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Center Management Portal
        </PortalTitle>
        <PortalSubtitle
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Your all-in-one solution for managing centers, staff, students, and operations with powerful tools and analytics.
        </PortalSubtitle>
      </Header>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.03 }}
            >
              <FeatureIcon
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1 * index, type: "spring" }}
                whileHover={{ rotate: 10, scale: 1.1 }}
              >
                {feature.icon}
              </FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </motion.div>

      <DashboardPreview
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.8 }}
        whileHover={{ scale: 1.01 }}
      >
        <DashboardImage
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
          alt="Dashboard Preview"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.8 }}
        />
      </DashboardPreview>

      <CTA
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8, duration: 0.6 }}
      >
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#1e40af' }}>Ready to Transform Your Center Management?</h2>
        <p style={{ fontSize: '1.1rem', color: '#475569', marginBottom: '2rem', maxWidth: '700px', marginLeft: 'auto', marginRight: 'auto' }}>
          Join hundreds of centers already using our portal to streamline their operations and improve efficiency.
        </p>
        <motion.button
          style={{
            padding: '1rem 2rem',
            background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
          whileHover={{ scale: 1.05, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
          whileTap={{ scale: 0.98 }}
        >
          Get Started Today
        </motion.button>
      </CTA>
    </PortalContainer>
  );
};

export default CenterPortal;